/**
 * URL state utilities for syncing calendar week and filters with the browser URL.
 *
 * URL format:
 *   /?week=2026-04-06&filters=friel,triclub,other
 *
 * - `week`:    ISO date string (YYYY-MM-DD) for the Monday of the displayed week.
 *              Omitted when the displayed week is the current week.
 * - `filters`: Comma-separated list of active filter keys: "friel", "triclub", "other".
 *              Omitted when all three are active (default).
 */

export const DEFAULT_FILTERS = { showFriel: true, showTriClub: true, showOther: true };

const FILTER_KEY_MAP = {
  friel: 'showFriel',
  triclub: 'showTriClub',
  other: 'showOther',
};

/**
 * Returns the Monday of the week containing `date` as a plain Date (time zeroed).
 * @param {Date} date
 * @returns {Date}
 */
export function getMondayOf(date) {
  const d = new Date(date);
  const dayOfWeek = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - dayOfWeek);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Format a Date as YYYY-MM-DD (local calendar date, no timezone conversion).
 * @param {Date} date
 * @returns {string}
 */
export function formatDateParam(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parse a YYYY-MM-DD string into a Date (local midnight). Returns null if invalid.
 * @param {string} str
 * @returns {Date|null}
 */
export function parseDateParam(str) {
  if (!str || !/^\d{4}-\d{2}-\d{2}$/.test(str)) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, m - 1, d);
  // Validate that the date didn't overflow (e.g., Feb 30)
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
    return null;
  }
  return date;
}

/**
 * Returns true if the filters object matches the defaults (all true).
 * @param {{ showFriel: boolean, showTriClub: boolean, showOther: boolean }} filters
 * @returns {boolean}
 */
export function areDefaultFilters(filters) {
  return filters.showFriel && filters.showTriClub && filters.showOther;
}

/**
 * Parse filter state from a comma-separated URL param string.
 * Returns DEFAULT_FILTERS if the string is absent/invalid.
 * @param {string|null} param  e.g. "friel,triclub"
 * @returns {{ showFriel: boolean, showTriClub: boolean, showOther: boolean }}
 */
export function parseFiltersParam(param) {
  if (!param) return { ...DEFAULT_FILTERS };
  const parts = param.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  if (parts.length === 0) return { ...DEFAULT_FILTERS };

  const result = { showFriel: false, showTriClub: false, showOther: false };
  let hasValidKey = false;
  for (const part of parts) {
    if (FILTER_KEY_MAP[part]) {
      result[FILTER_KEY_MAP[part]] = true;
      hasValidKey = true;
    }
  }
  return hasValidKey ? result : { ...DEFAULT_FILTERS };
}

/**
 * Serialize filter state to a comma-separated URL param string.
 * Returns null if all filters are active (default — param omitted).
 * @param {{ showFriel: boolean, showTriClub: boolean, showOther: boolean }} filters
 * @returns {string|null}
 */
export function serializeFiltersParam(filters) {
  if (areDefaultFilters(filters)) return null;
  const active = Object.entries(FILTER_KEY_MAP)
    .filter(([, stateKey]) => filters[stateKey])
    .map(([urlKey]) => urlKey);
  return active.length > 0 ? active.join(',') : null;
}

/**
 * Build a URL search string from week date and filters.
 * Returns '' if both are defaults.
 * @param {Date} weekMonday  - Monday of the week to show (Date)
 * @param {{ showFriel: boolean, showTriClub: boolean, showOther: boolean }} filters
 * @returns {string}  e.g. "?week=2026-04-06&filters=friel"
 */
export function buildUrlSearch(weekMonday, filters) {
  const params = new URLSearchParams();

  const todayMonday = getMondayOf(new Date());
  const weekStr = formatDateParam(getMondayOf(weekMonday));
  const todayStr = formatDateParam(todayMonday);
  if (weekStr !== todayStr) {
    params.set('week', weekStr);
  }

  const filtersParam = serializeFiltersParam(filters);
  if (filtersParam) {
    params.set('filters', filtersParam);
  }

  const str = params.toString();
  return str ? `?${str}` : '';
}

/**
 * Read the initial week date from the current URL (window.location.search).
 * Falls back to today's date if the param is absent or invalid.
 * @returns {Date}
 */
export function readWeekFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const weekStr = params.get('week');
  if (weekStr) {
    const parsed = parseDateParam(weekStr);
    if (parsed) return getMondayOf(parsed);
  }
  return getMondayOf(new Date());
}

/**
 * Read the initial filters from the current URL (window.location.search).
 * Falls back to DEFAULT_FILTERS if the param is absent or invalid.
 * @returns {{ showFriel: boolean, showTriClub: boolean, showOther: boolean }}
 */
export function readFiltersFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return parseFiltersParam(params.get('filters'));
}
